/**
 * =====================================================================================
 * STATSIG MANAGER - Módulo central de gerenciamento de experimentos e modelos dinâmicos
 * =====================================================================================
 *
 * OBJETIVO:
 * Este módulo encapsula a integração com o Statsig, oferecendo uma interface única para:
 *  - Inicializar o StatsigClient e controlar seu estado (ready).
 *  - Gerenciar gates e acessar layers com validações opcionais.
 *  - Enviar payloads ricos e consistentes para APIs de modelos dinâmicos.
 *  - Persistir e reutilizar dados relevantes para evitar múltiplas chamadas.
 *
 * FUNCIONALIDADES AVANÇADAS:
 * - Controle de envio via `shouldSendPayload(triggers, modelKey, mode)`:
 *    -> Triggers suportados: "first_load", "stable_id", "login", "utms", "no_persisted_model",
 *       além de condições como "collections+3", "products+2", "cart+1".
 *    -> Modos: "some" (default) ou "every", para condicionar o envio a múltiplos fatores.
 * - Persistência inteligente de modelo, com fallback seguro de `localStorage`.
 * - Enriquecimento de payload com dados de dispositivo, utms, carrinho e cliente.
 * - Suporte a migração de keys antigas de experimento com logs.
 *
 * EXPORTS PRINCIPAIS:
 * - getOrCreateStatsigManager(): instancia (ou reutiliza) o ExperimentManager.
 * - getStatsigClient(): retorna o client Statsig atual.
 * - buildModelRequestPayload(): monta o corpo da requisição para o modelo.
 * - ModelManager.getModel(): executa POST com payload enriquecido + persistência.
 * - getCachedDevice(): coleta e salva dados de device uma única vez por sessão.
 * - shouldSendPayload(): avalia condições para controlar se o payload deve ser enviado.
 *
 * RECOMENDAÇÕES:
 * - Evite duplicar lógica de build e envio nos testes. Use este módulo como camada única.
 * - Use `Logger.error` para logar falhas de rede, persistência ou parsing.
 * - Padronize a verificação de triggers de envio com `shouldSendPayload`.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/**
 * Logger utilitário com controle de exibição por escopo.
 * Ativado por localStorage.setItem("DEBUG:{scope}", "true") para logs por experimento/escopo.
 */
function isDebugEnabled(scope) {
    var _a;
    if (typeof window === "undefined" || !scope)
        return false;
    return ((_a = window.localStorage) === null || _a === void 0 ? void 0 : _a.getItem(`DEBUG:${scope}`)) === "true";
}
export function syncDebugFlag(scope, debug) {
    const key = `DEBUG:${scope}`;
    if (debug) {
        localStorage.setItem(key, "true");
    }
    else {
        localStorage.removeItem(key);
    }
}
export const Logger = {
    log: (scope, ...args) => {
        if (isDebugEnabled(scope))
            console.log(...args);
    },
    warn: (scope, ...args) => {
        if (isDebugEnabled(scope))
            console.warn(...args);
    },
    error: (...args) => {
        console.error(...args);
    },
    group: (scope, ...args) => {
        if (isDebugEnabled(scope))
            console.group(...args);
    },
    groupEnd: (scope) => {
        if (isDebugEnabled(scope)) {
            console.groupEnd();
        }
    }
};
// Singleton StatsigExperimentManager instance
let statsigExperimentManagerInstance = null;
// Singleton de StatsigClient (para garantir que só exista uma instância)
let statsigClientInstance = null;
/**
 * Classe abstrata base para gerenciamento de experimentos.
 * Define a interface mínima que qualquer implementação concreta (ex: StatsigExperimentManager) deve seguir.
 */
class ExperimentManager {
    constructor() {
        if (new.target === ExperimentManager) {
            throw new Error("[Statsig][Manager] Classe abstrata ExperimentManager não pode ser instanciada diretamente.");
        }
        this.provider = null;
    }
}
/**
 * Inicialização automática do StatsigClient caso já esteja disponível no `window`.
 * Define o client global para evitar múltiplas inicializações em testes.
 */
if (typeof window !== 'undefined') {
    const candidateClient = window.__statsigClient ||
        window.statsigClient;
    if (candidateClient && typeof candidateClient.getLayer === 'function') {
        setStatsigClient(candidateClient);
    }
    /**
      Inicializa o Statsig com a chave de inicialização.
      A chave é definida no settings do tema e é usada para autenticar as requisições.
    */
    getCachedDevice();
}
/**
 * Implementação concreta de ExperimentManager usando o StatsigClient.
 * Responsável por verificar gates e obter parâmetros de layers.
 * Utiliza o client do Statsig para acessar features experimentais.
 */
export class StatsigExperimentManager extends ExperimentManager {
    static waitForReady() {
        throw new Error('Method not implemented.');
    }
    constructor(statsigClient) {
        super();
        this.provider = 'statsig';
        this.client = statsigClient;
        this._readyPromise = null;
    }
    // Aguarda o StatsigClient ficar ready, com fallback por polling e timeout
    waitForReady() {
        return __awaiter(this, arguments, void 0, function* (timeoutMs = 20000) {
            if (this._readyPromise)
                return this._readyPromise;
            this._readyPromise = new Promise((resolve, reject) => {
                let resolved = false;
                const clearAll = () => {
                    clearTimeout(timeoutId);
                };
                const checkClientReady = () => {
                    const client = getStatsigClient();
                    if (client && client.loadingStatus === "Ready") {
                        if (!resolved) {
                            resolved = true;
                            clearAll();
                            resolve();
                        }
                        return true;
                    }
                    return false;
                };
                // Primeira verificação imediata
                if (checkClientReady())
                    return;
                // Polling a cada 300ms, com timeout total
                const intervalId = setInterval(() => {
                    if (checkClientReady()) {
                        clearInterval(intervalId);
                    }
                }, 300);
                const timeoutId = setTimeout(() => {
                    if (!resolved) {
                        resolved = true;
                        clearInterval(intervalId);
                        clearAll();
                        Logger.error(`[Statsig][Manager] Timeout waiting for client to be ready`, timeoutMs);
                        reject(new Error(`[Statsig][Manager] Timeout of ${timeoutMs}ms`));
                    }
                }, timeoutMs);
            });
            return this._readyPromise;
        });
    }
    // Verifica se um gate está ativo
    checkGate(gateName) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                yield this.waitForReady();
                return this.client.checkGate(gateName);
            }
            catch (error) {
                Logger.error("[Statsig][Manager] Failed to check gate", error);
                return false;
            }
        });
    }
    // Obtém os parâmetros de uma layer, com suporte opcional a verificação de um gate
    getLayerParams(layerName_1) {
        return __awaiter(this, arguments, void 0, function* (layerName, options = {}) {
            const { shouldCheckGate = false, gateName = null, params = null } = options;
            try {
                yield this.waitForReady();
                if (shouldCheckGate && gateName) {
                    const gateResult = yield this.checkGate(gateName);
                    if (!gateResult)
                        return null;
                }
                if (params && typeof this.client.getLayer === "function") {
                    return this.client.getLayer(layerName, params);
                }
                return this.client.getLayer(layerName);
            }
            catch (error) {
                Logger.error("[Statsig][Manager] Failed to get layer parameters", error);
                return null;
            }
        });
    }
    // ===========================
    // Compatibilidade com testes de placement
    // ===========================
    // Alias para compatibilidade com legacy: waitForInitialization -> waitForReady
    waitForInitialization() {
        return __awaiter(this, void 0, void 0, function* () {
            return this.waitForReady();
        });
    }
    // Exposição direta do StableID via getAsyncContext
    getStableID() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            try {
                yield this.waitForReady();
                const context = yield this.client.getAsyncContext();
                return (_a = context === null || context === void 0 ? void 0 : context.stableID) !== null && _a !== void 0 ? _a : null;
            }
            catch (error) {
                Logger.error("[Statsig][Manager] Failed to get stableID", error);
                return null;
            }
        });
    }
    // Alias para getLayerParams sem options (uso comum em placements)
    getLayer(layerName) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.getLayerParams(layerName);
        });
    }
}
/**
 * Classe responsável por intermediar chamadas a modelos dinâmicos externos.
 * Gera, envia e persiste o payload enriquecido com dados contextuais.
 * Controla timeout e armazena dados da requisição e resposta no localStorage.
 */
export class ModelManager {
    getModel(modelUrl_1, data_1) {
        return __awaiter(this, arguments, void 0, function* (modelUrl, data, settings = { timeout: 5000 }, modelKey, includeFields = []) {
            var _a;
            const fetchWithTimeout = (url, options = {}, timeout = 5000) => {
                const controller = new AbortController();
                const timeoutPromise = new Promise((_, reject) => setTimeout(() => {
                    controller.abort();
                    Logger.error("[Statsig][Manager] Timeout waiting for fetch", timeout);
                    reject(new Error(`[Statsig][Manager] Timeout of ${timeout}ms`));
                }, timeout));
                return Promise.race([
                    fetch(url, Object.assign(Object.assign({}, options), { signal: controller.signal })),
                    timeoutPromise,
                ]);
            };
            const requestBody = buildModelRequestPayload(data, includeFields);
            try {
                const res = yield fetchWithTimeout(modelUrl + "/pull/", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(requestBody),
                }, settings.timeout);
                if (!res.ok)
                    throw new Error(`HTTP error! Status: ${res.status}`);
                const modelData = yield res.json();
                // Persist request in localStorage
                const payloadToPersist = {
                    timestamp: new Date().toISOString(),
                    modelUrl,
                    payload: requestBody,
                };
                if (modelKey) {
                    try {
                        localStorage.setItem(`${modelKey}:request`, JSON.stringify(payloadToPersist));
                    }
                    catch (error) {
                        Logger.warn(`[Statsig][Manager] Failed to persist model request using modelKey: ${modelKey}`, error);
                    }
                    // Persist the model itself if modelKey is present
                    const enrichedModel = Object.assign(Object.assign({}, modelData === null || modelData === void 0 ? void 0 : modelData.data), { modelUrl, savedAt: Date.now() });
                    try {
                        localStorage.setItem(modelKey, JSON.stringify(enrichedModel));
                    }
                    catch (error) {
                        Logger.warn(`[Statsig][Manager] Failed to persist model response using modelKey: ${modelKey}`, error);
                    }
                }
                Logger.log("[Statsig][Manager] Payload fetched successfully to model: ", {
                    requestBody,
                    modelUrl,
                    modelData
                });
                return (_a = modelData === null || modelData === void 0 ? void 0 : modelData.data) !== null && _a !== void 0 ? _a : null;
            }
            catch (error) {
                Logger.error("[Statsig][Manager] Failed to fetch model data", error);
                return null;
            }
        });
    }
}
/**
 * Função para definir a instância do StatsigClient (chamada após o init)
 * Define a instância global do StatsigClient, tornando-a acessível via window.
 * Deve ser chamada após a inicialização do StatsigClient.
 * @param {StatsigClient} client - Instância do StatsigClient já inicializada.
 */
export function setStatsigClient(client) {
    statsigClientInstance = client;
    if (typeof window !== 'undefined') {
        // Salva em ambos os lugares para máxima compatibilidade
        window.__statsigClient = client;
        window.statsigClient = client;
        // Logger.debug("[Statsig][Manager] StatsigClient set in window", client);
    }
}
/**
 * Função para obter a instância atual do StatsigClient
 * Retorna a instância atual do StatsigClient, tentando obter do singleton ou do window.
 * @returns {StatsigClient | null} A instância do StatsigClient, ou null se não disponível.
 */
export function getStatsigClient() {
    // Se já temos uma instância, retorna ela
    if (statsigClientInstance)
        return statsigClientInstance;
    // Checa no window por __statsigClient OU statsigClient
    if (typeof window !== 'undefined') {
        const candidateClient = window.__statsigClient ||
            window.statsigClient;
        if (candidateClient && typeof candidateClient.getLayer === 'function') {
            setStatsigClient(candidateClient);
            return statsigClientInstance;
        }
    }
    return null;
}
/**
 * Função para obter o dispositivo do usuário, cacheando as informações
 * Coleta e retorna as informações do dispositivo do usuário, com cache em localStorage.
 * Evita coleta repetida e detecta mudanças de device.
 * @returns {ModelRequestData["device"]} Objeto com informações detalhadas do dispositivo.
 */
export function getCachedDevice() {
    var _a;
    const key = "statsig:device";
    const storage = getSafeStorage();
    // Logger.log("[Statsig][Manager] Collecting device information...");
    const userAgent = navigator.userAgent;
    const browserName = detectBrowserName(userAgent);
    const newInfo = {
        userAgent,
        platform: navigator.platform,
        language: navigator.language,
        screenWidth: window.screen.width,
        screenHeight: window.screen.height,
        deviceMemory: navigator.deviceMemory,
        hardwareConcurrency: navigator.hardwareConcurrency,
        touchSupport: 'ontouchstart' in window ? 'full' : 'none',
        connectionType: ((_a = navigator.connection) === null || _a === void 0 ? void 0 : _a.effectiveType) || 'unknown',
        deviceType: detectDeviceType(userAgent),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        colorSchemePreference: window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light',
        pixelRatio: window.devicePixelRatio,
        isStandalonePWA: window.matchMedia('(display-mode: standalone)').matches,
        browserName,
    };
    try {
        const cached = storage.getItem(key);
        if (cached) {
            const parsed = JSON.parse(cached);
            // Logger.log("[Statsig][Manager] Device info found in cache:", parsed);
            if (JSON.stringify(parsed) === JSON.stringify(newInfo)) {
                // Logger.log("[Statsig][Manager] Device info unchanged — using cached version");
                return parsed;
            }
        }
    }
    catch (error) {
        Logger.error("[Statsig][Manager] Error reading device from localStorage", error);
    }
    const now = new Date();
    const lastUpdate = now.toISOString();
    const lastUpdateLocal = now.toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" });
    newInfo.lastUpdate = lastUpdate;
    newInfo.lastUpdateLocal = lastUpdateLocal;
    storage.setItem(key, JSON.stringify(newInfo));
    storage.setItem("statsig:device:lastupdate", lastUpdate);
    // Logger.log("[Statsig][Manager] Device info saved to localStorage", newInfo);
    return newInfo;
}
/**
 * Interface para os dados do request do modelo
 * Monta o payload de requisição para o modelo dinâmico, incluindo apenas os campos especificados.
 * Busca dados enriquecidos do storage e do contexto do usuário.
 * @param {ModelRequestData} data - Dados base do usuário/experimento.
 * @param {PayloadField[]} [includeFields=[]] - Campos a incluir no payload.
 * @returns {Partial<ModelRequestData>} Objeto parcial com os campos solicitados.
 */
export function buildModelRequestPayload(data, includeFields = []) {
    const storage = getSafeStorage();
    const payload = {};
    const payloadBuilders = {
        customer_id: () => data.customer_id || undefined,
        stable_id: () => data.stable_id,
        allocated_experiment: () => data.allocated_experiment || undefined,
        cart: () => getCartData() || "[]",
        cartItemsIds: () => getCartItemsIdsString() || "[]",
        utms: () => JSON.parse(storage.getItem("statsig:utms") || "[]"),
        products: () => JSON.parse(storage.getItem("statsig:products") || "[]"),
        collections: () => JSON.parse(storage.getItem("statsig:collections") || "[]"),
        customer: () => ({
            infos: JSON.parse(storage.getItem("statsig:customer:infos") || "[]"),
            orders: JSON.parse(storage.getItem("statsig:customer:orders") || "[]")
        }),
        device: () => getCachedDevice()
    };
    includeFields.forEach((field) => {
        const builder = payloadBuilders[field];
        if (builder) {
            const value = builder();
            if (value !== null) {
                payload[field] = value;
            }
        }
    });
    return payload;
}
/**
 * Função utilitária para obter a string de cartItemsIds a partir do Shopify.cartItems
 * Retorna uma string formatada dos IDs dos itens do carrinho, no padrão "product_id:variant_id-quantity,...".
 * Útil para rastreamento e análises de carrinho.
 * @returns {string} String dos itens do carrinho, ou string vazia se vazio/indisponível.
 */
export function getCartItemsIdsString() {
    var _a, _b;
    try {
        const cartItems = (_b = (_a = window === null || window === void 0 ? void 0 : window.Shopify) === null || _a === void 0 ? void 0 : _a.cartItems) !== null && _b !== void 0 ? _b : [];
        if (!Array.isArray(cartItems) || cartItems.length === 0) {
            Logger.warn('[Statsig][Manager] Cart is empty or unavailable');
            return '';
        }
        // Formato esperado: "product_id:variant_id-quantity,product_id:variant_id-quantity"
        return cartItems
            .map((item) => `${item.product_id}:${item.variant_id}-${item.quantity}`)
            .join(',');
    }
    catch (error) {
        Logger.error('[Statsig][Manager] Failed to parse Shopify.cartItems', error);
        return '';
    }
}
/**
 * Função utilitária para obter os dados do carrinho no formato esperado
 * Retorna os dados do carrinho no formato [{ sku, qty }], extraídos de Shopify.cartItems.
 * @returns {unknown[]} Array de objetos do carrinho, ou array vazio se indisponível.
 */
export function getCartData() {
    var _a, _b;
    try {
        const cartItems = (_b = (_a = window === null || window === void 0 ? void 0 : window.Shopify) === null || _a === void 0 ? void 0 : _a.cartItems) !== null && _b !== void 0 ? _b : [];
        if (!Array.isArray(cartItems) || cartItems.length === 0) {
            Logger.warn('[Statsig][Manager] Cart is empty or unavailable');
            return [];
        }
        return cartItems.map((item) => ({
            sku: item.sku,
            qty: item.quantity
        }));
    }
    catch (error) {
        Logger.error('[Statsig][Manager] Failed to parse Shopify.cartItems', error);
        return [];
    }
}
/**
 * Função para aguardar o StatsigClient ficar pronto, com timeout e polling
 * Aguarda o StatsigClient ficar pronto (loadingStatus === 'Ready'), usando polling e timeout.
 * @param {number} [timeoutMs=5000] - Tempo máximo de espera em milissegundos.
 * @returns {Promise<StatsigClient>} Promise resolvida com o StatsigClient pronto.
 * @throws {Error} Se o timeout expirar antes do client estar pronto.
 */
export function waitForStatsigClientReady() {
    return __awaiter(this, arguments, void 0, function* (timeoutMs = 5000) {
        const pollInterval = 100;
        let waited = 0;
        return new Promise((resolve, reject) => {
            const interval = setInterval(() => {
                const client = getStatsigClient();
                if (client && client.loadingStatus === 'Ready') {
                    clearInterval(interval);
                    resolve(client);
                }
                else {
                    waited += pollInterval;
                    if (waited >= timeoutMs) {
                        clearInterval(interval);
                        Logger.error('[Statsig] Timeout waiting for client to be ready', timeoutMs);
                        reject(new Error('[Statsig] Timeout waiting for client to be ready'));
                    }
                }
            }, pollInterval);
        });
    });
}
/**
 * Função para obter ou criar uma instância do StatsigExperimentManager
 * Obtém ou cria uma instância singleton do StatsigExperimentManager pronta para uso.
 * Aguarda a inicialização do StatsigClient antes de instanciar o manager.
 * @param {number} [timeoutMs=5000] - Tempo máximo de espera pelo StatsigClient.
 * @returns {Promise<StatsigExperimentManager>} Promise resolvida com o manager.
 * @throws {Error} Se o StatsigClient não estiver pronto ou não for válido.
 */
export function getOrCreateStatsigManager() {
    return __awaiter(this, arguments, void 0, function* (timeoutMs = 5000) {
        if (statsigExperimentManagerInstance)
            return statsigExperimentManagerInstance;
        let client;
        try {
            client = yield waitForStatsigClientReady(timeoutMs);
        }
        catch (error) {
            throw new Error('[Statsig] Client not properly initialized or ready: ' + ((error === null || error === void 0 ? void 0 : error.message) || error));
        }
        if (!(client === null || client === void 0 ? void 0 : client.checkGate)) {
            throw new Error('[Statsig] Gate not available in client');
        }
        // Logger.debug('[Statsig][Manager] Client initialized');
        statsigExperimentManagerInstance = new StatsigExperimentManager(client);
        return statsigExperimentManagerInstance;
    });
}
/**
 * Detecta o tipo de dispositivo com base no userAgent.
 * @param {string} ua - UserAgent do navegador.
 * @returns {'mobile' | 'tablet' | 'desktop'} Tipo de dispositivo detectado.
 */
function detectDeviceType(ua) {
    ua = ua.toLowerCase();
    if (/tablet|ipad|playbook|silk/.test(ua))
        return 'tablet';
    if (/mobi|android|iphone|ipod/.test(ua))
        return 'mobile';
    return 'desktop';
}
/**
 * Detecta o nome do navegador baseado no userAgent.
 * Reconhece também webviews de redes sociais.
 * @param {string} userAgent - UserAgent completo do navegador.
 * @returns {string} Nome amigável do navegador ou 'Unknown'.
 */
function detectBrowserName(userAgent) {
    if (/instagram/i.test(userAgent))
        return 'Instagram WebView';
    if (/FBAN|FBAV/i.test(userAgent))
        return 'Facebook WebView';
    if (/WhatsApp/i.test(userAgent))
        return 'WhatsApp WebView';
    if (/Line/i.test(userAgent))
        return 'LINE WebView';
    if (/Snapchat/i.test(userAgent))
        return 'Snapchat WebView';
    if (/TikTok/i.test(userAgent))
        return 'TikTok WebView';
    if (/Twitter/i.test(userAgent))
        return 'Twitter WebView';
    if (/LinkedInApp/i.test(userAgent))
        return 'LinkedIn WebView';
    if (/OPR\//i.test(userAgent))
        return 'Opera';
    if (/Edg\//i.test(userAgent))
        return 'Edge';
    if (/CriOS/i.test(userAgent))
        return 'Chrome (iOS)';
    if (/FxiOS/i.test(userAgent))
        return 'Firefox (iOS)';
    if (/Chrome\/\d+/i.test(userAgent) && !/Edge|Edg|OPR|Instagram|FBAV|FBAN/i.test(userAgent))
        return 'Chrome';
    if (/Safari\/\d+/i.test(userAgent) && !/Chrome|CriOS|FxiOS/i.test(userAgent))
        return 'Safari';
    if (/Firefox\/\d+/i.test(userAgent))
        return 'Firefox';
    return 'Unknown';
}
/**
 * Fallback-safe localStorage/sessionStorage utility
 * Retorna um objeto `Storage` seguro para uso, preferencialmente `localStorage`, com fallback para `sessionStorage` ou memória.
 * Evita exceções em ambientes restritos (ex: Safari privado).
 * @returns {Storage} Uma instância segura de armazenamento.
 */
export function getSafeStorage() {
    try {
        return window.localStorage;
    }
    catch (e) {
        try {
            return window.sessionStorage;
        }
        catch (e) {
            // fallback em memória, não persistente
            return {
                getItem: () => null,
                setItem: () => { },
                removeItem: () => { },
                clear: () => { },
                key: () => null,
                length: 0,
            };
        }
    }
}
/**
 * Função para verificar se deve enviar o payload baseado nas condições
 * Avalia uma lista de triggers e determina se o payload do experimento deve ser enviado.
 * Suporta modo 'some' (default) ou 'every' para combinar múltiplas condições.
 * @param {string[]} triggers - Lista de condições como 'first_load', 'login', 'stable_id', etc.
 * @param {string} modelKey - Chave do modelo no localStorage.
 * @param {"some" | "every"} [mode='some'] - Modo de avaliação.
 * @returns {boolean} true se as condições forem atendidas conforme o modo especificado.
 */
export function shouldSendPayload(triggers, modelKey, mode = "some") {
    /**
     * Loga o resultado de um trigger no Logger.
     * @param {string} trigger - Nome do trigger.
     * @param {boolean} result - Resultado da avaliação.
     */
    function log(trigger, result) {
        Logger.log(`[Statsig][${modelKey}] ${trigger}: ${result}`);
    }
    /**
     * Verifica se o usuário fez login desde a última verificação.
     * Usa infos do customer e compara com o último update.
     * @returns {boolean} true se houve login, false caso contrário.
     */
    function hasLoggedIn() {
        const storage = getSafeStorage();
        try {
            const current = storage.getItem("statsig:customer:infos");
            const previous = storage.getItem(`${current}:trigger:lastupdate`);
            const result = !!(current && current !== previous);
            if (result)
                storage.setItem(`${current}:trigger:lastupdate`, current || "");
            // log("login", result);
            return result;
        }
        catch (_a) {
            // log("login", false);
            return false;
        }
    }
    /**
     * Verifica se é o primeiro carregamento do stable_id para este modelo.
     * Usa uma flag persistida no localStorage.
     * @param {string} modelKey - Chave única do modelo.
     * @returns {boolean} true se for o primeiro load do stable_id, false caso contrário.
     */
    function isFirstStableIdLoad(modelKey) {
        const storage = getSafeStorage();
        try {
            const key = `${modelKey}:trigger:stableid`;
            const stableId = storage.getItem("STATSIG_LOCAL_STORAGE_STABLE_ID");
            const stableIdUsed = storage.getItem(key);
            const result = !!stableId && stableId !== stableIdUsed;
            if (result)
                storage.setItem(key, stableId);
            return result;
        }
        catch (_a) {
            return false;
        }
    }
    /**
     * Verifica se o usuário visitou N coleções novas desde a última verificação.
     * @param {number} [threshold=3] - Quantidade mínima de coleções.
     * @returns {boolean} true se atingiu o threshold, false caso contrário.
     */
    function hasVisitedNCollections(threshold = 3) {
        const storage = getSafeStorage();
        try {
            const key = "statsig:collections";
            const last = JSON.parse(storage.getItem(`${key}:trigger:lastupdate`) || "[]");
            const current = JSON.parse(storage.getItem(key) || "[]");
            const delta = current.filter((c) => !last.includes(c));
            const result = delta.length >= threshold;
            if (result)
                storage.setItem(`${key}:trigger:lastupdate`, JSON.stringify(current));
            // log(`collections+${threshold}`, result);
            return result;
        }
        catch (_a) {
            // log(`collections+${threshold}`, false);
            return false;
        }
    }
    /**
     * Verifica se o usuário visitou N produtos novos desde a última verificação.
     * @param {number} [threshold=3] - Quantidade mínima de produtos.
     * @returns {boolean} true se atingiu o threshold, false caso contrário.
     */
    function hasVisitedNProducts(threshold = 3) {
        const storage = getSafeStorage();
        try {
            const key = "statsig:products";
            const last = JSON.parse(storage.getItem(`${key}:trigger:lastupdate`) || "[]");
            const current = JSON.parse(storage.getItem(key) || "[]");
            const delta = current.filter((p) => !last.includes(p));
            const result = delta.length >= threshold;
            if (result)
                storage.setItem(`${key}:trigger:lastupdate`, JSON.stringify(current));
            // log(`products+${threshold}`, result);
            return result;
        }
        catch (_a) {
            // log(`products+${threshold}`, false);
            return false;
        }
    }
    /**
     * Verifica se houve atualização dos parâmetros de UTM.
     * @returns {boolean} true se utms foram atualizados, false caso contrário.
     */
    function hasUpdatedUtms() {
        const storage = getSafeStorage();
        try {
            const key = "statsig:utms";
            const current = storage.getItem(key);
            const last = storage.getItem(`${key}:trigger:lastupdate`);
            const result = !!(current && current !== last);
            if (result)
                storage.setItem(`${key}:trigger:lastupdate`, current || "");
            // log("utms", result);
            return result;
        }
        catch (_a) {
            // log("utms", false);
            return false;
        }
    }
    /**
     * Verifica se houve aumento na quantidade de itens do carrinho desde a última verificação.
     * @param {number} [threshold=1] - Quantidade mínima de aumento.
     * @returns {boolean} true se o carrinho aumentou pelo menos threshold, false caso contrário.
     */
    function hasCartIncreased(threshold = 1) {
        const storage = getSafeStorage();
        try {
            const current = storage.getItem("statsig:products") || "[]";
            const previous = storage.getItem("statsig:cart") || "[]";
            const currentList = JSON.parse(current);
            const previousList = JSON.parse(previous);
            const result = (currentList.length - previousList.length) >= threshold;
            if (result)
                storage.setItem("statsig:cart", current);
            // log(`cart+${threshold}`, result);
            return result;
        }
        catch (_a) {
            // log(`cart+${threshold}`, false);
            return false;
        }
    }
    /**
     * Verifica se é o primeiro carregamento da página para este modelo.
     * Usa uma flag persistida no localStorage.
     * @param {string} modelKey - Chave única do modelo.
     * @returns {boolean} true se for o primeiro load, false caso contrário.
     */
    function isFirstLoad(modelKey) {
        const storage = getSafeStorage();
        try {
            const key = `${modelKey}:trigger:firstload`;
            const alreadyLoaded = storage.getItem(key);
            const result = !alreadyLoaded;
            if (result)
                storage.setItem(key, "1");
            // log("first_load", result);
            return result;
        }
        catch (_a) {
            // log("first_load", false);
            return false;
        }
    }
    /**
     * Verifica se não existe modelo persistido no localStorage.
     * @param {string} modelKey - Chave do modelo.
     * @returns {boolean} true se não houver modelo persistido, false caso contrário.
     */
    function noPersistedModel(modelKey) {
        const storage = getSafeStorage();
        try {
            const persisted = storage.getItem(modelKey);
            const parsed = JSON.parse(persisted || "null");
            const result = !parsed || typeof parsed !== "object" || !parsed.savedAt;
            return result;
        }
        catch (_a) {
            return false;
        }
    }
    const results = triggers.map(trigger => {
        switch (trigger) {
            case "first_load":
                return isFirstLoad(modelKey);
            case "stable_id":
                return isFirstStableIdLoad(modelKey);
            case "login":
                return hasLoggedIn();
            case "utms":
                return hasUpdatedUtms();
            case "no_persisted_model":
                return noPersistedModel(modelKey);
            default:
                if (trigger.startsWith("collections+")) {
                    return hasVisitedNCollections(parseInt(trigger.split("+")[1]));
                }
                if (trigger.startsWith("products+")) {
                    return hasVisitedNProducts(parseInt(trigger.split("+")[1]));
                }
                if (trigger.startsWith("cart+")) {
                    return hasCartIncreased(parseInt(trigger.split("+")[1]));
                }
                log(trigger, false);
                return false;
        }
    });
    return mode === "every" ? results.every(Boolean) : results.some(Boolean);
}
/**
 * Retorna um identificador sincronizado, priorizando o stable_id (do Statsig).
 * Fallback para customer_id se stable_id estiver ausente.
 */
export function getSyncedUserId(data) {
    if (data.stable_id) {
        return { stable_id: data.stable_id };
    }
    return { customer_id: data.customer_id };
}
